import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import {
  PIECES,
  PIECE_IDS,
  loadPieceGeometry,
  pieceOrigin,
  pieceScale,
  terrainTriangles,
  type ArenaMap,
  type PieceGeometry,
  type PieceId,
  type PlacedPiece,
} from '@arena/shared';
import {
  DEFAULT_GROUND,
  FLOORS,
  SWAYING,
  groundColors,
  mottle,
  sway,
} from './environment';

const loader = new GLTFLoader();
const models = new Map<string, Promise<GLTF>>();
/** One texture per kit's colormap, so the GPU holds each once rather than once per model. */
const colormaps = new Map<string, THREE.Texture>();

export function loadModel(name: string): Promise<GLTF> {
  let model = models.get(name);
  if (!model) {
    model = loader.loadAsync(`/models/${name}.glb`).then((gltf) => {
      gltf.scene.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        child.castShadow = true;
        child.receiveShadow = true;
        // Every Kenney GLB is one colormap, fully rough and non-metallic: Lambert lights it the same for less.
        const { map, color, side } =
          child.material as THREE.MeshStandardMaterial;
        const kit = `${name.slice(0, name.lastIndexOf('/') + 1)}${map?.name}`;
        if (map && !colormaps.has(kit)) colormaps.set(kit, map);
        child.material = new THREE.MeshLambertMaterial({
          map: map && colormaps.get(kit),
          color,
          side,
        });
        if (FLOORS.has(name as PieceId)) mottle(child.material, true);
      });
      return gltf;
    });
    models.set(name, model);
  }
  return model;
}

export type PieceTemplates = Partial<Record<PieceId, THREE.Object3D>>;

/** Wraps each file a loader fetches, for a loading bar. */
export type Track = <T>(promise: Promise<T>) => Promise<T>;
const untracked: Track = (promise) => promise;

export async function loadPieceTemplates(
  ids: readonly PieceId[] = PIECE_IDS,
  track = untracked,
): Promise<PieceTemplates> {
  const entries = await Promise.all(
    ids.map(async (id) => [id, (await track(loadModel(id))).scene] as const),
  );
  return Object.fromEntries(entries);
}

/** The pieces a map places, so a match only downloads those. */
export const mapPieceIds = (map: ArenaMap): PieceId[] => [
  ...new Set(map.pieces.map((p) => p.piece)),
];

const glbs = new Map<string, Promise<ArrayBuffer>>();
const fetchGlb = (file: string): Promise<ArrayBuffer> => {
  let glb = glbs.get(file);
  if (!glb) {
    glb = fetch(`/models/${file}`).then((r) => r.arrayBuffer());
    glbs.set(file, glb);
  }
  return glb;
};

/** The same triangles the server collides against, read from the same GLBs. */
export const loadCollisionGeometry = (
  ids: readonly PieceId[],
  track = untracked,
): Promise<PieceGeometry> =>
  loadPieceGeometry((file) => track(fetchGlb(file)), ids);

export function placePiece(object: THREE.Object3D, placed: PlacedPiece): void {
  const origin = pieceOrigin(placed);
  object.position.set(origin.x, origin.y, origin.z);
  object.rotation.set(0, (placed.rot * Math.PI) / 2, 0);
  object.scale.setScalar(pieceScale(placed));
}

export function buildMapObject(
  map: ArenaMap,
  templates: PieceTemplates,
): THREE.Group {
  const group = new THREE.Group();
  for (const [index, placed] of map.pieces.entries()) {
    const template = templates[placed.piece];
    if (!template) continue;
    const object = template.clone();
    object.userData.piece = placed.piece;
    object.userData.index = index;
    placePiece(object, placed);
    group.add(object);
  }
  if (map.terrain?.length) group.add(buildTerrain(map));
  paintGround(map, templates);
  return group;
}

const DEFAULT_TERRAIN = '#7da35a';
/** Terrain shade at level 0; it lightens higher up. */
const TERRAIN_SHADE = 0.94;

const floorColors = new Map<PieceId, THREE.Color>();

/** The palette texel most of a floor piece's vertices sample, which is what its surface shows. */
function floorColor(id: PieceId, template: THREE.Object3D): THREE.Color {
  const cached = floorColors.get(id);
  if (cached) return cached;
  const mesh = template.getObjectByProperty('isMesh', true) as THREE.Mesh;
  const material = mesh.material as THREE.MeshLambertMaterial;
  const image = material.map!.image as ImageBitmap;
  const uv = mesh.geometry.getAttribute('uv');
  const counts = new Map<string, number>();
  for (let i = 0; i < uv.count; i++) {
    const key = `${uv.getX(i)},${uv.getY(i)}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const [u, v] = [...counts]
    .sort((a, b) => b[1] - a[1])[0][0]
    .split(',')
    .map(Number);
  const context = Object.assign(document.createElement('canvas'), {
    width: 1,
    height: 1,
  }).getContext('2d')!;
  context.drawImage(
    image,
    Math.min(Math.floor(u * image.width), image.width - 1),
    Math.min(Math.floor(v * image.height), image.height - 1),
    1,
    1,
    0,
    0,
    1,
    1,
  );
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
  const color = new THREE.Color()
    .setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace)
    .multiply(material.color);
  floorColors.set(id, color);
  return color;
}

/**
 * Writes each tile's ground colour at level 0 (floor, else terrain, else grass) into
 * `groundColors`. Bare tiles under other pieces (walls, stairs, trees) get none, so walls and
 * stairs don't bleed the ground beneath them onto floors.
 */
export function paintGround(map: ArenaMap, templates: PieceTemplates): void {
  const ground = new THREE.Color(map.env?.ground ?? DEFAULT_GROUND);
  const terrain = new THREE.Color(
    map.env?.terrain ?? DEFAULT_TERRAIN,
  ).multiplyScalar(TERRAIN_SHADE);
  const tiles = new Map<string, THREE.Color | null>();
  for (const placed of map.pieces)
    if (placed.level === 0)
      tiles.set(`${Math.round(placed.x)},${Math.round(placed.z)}`, null);
  const corners = new Set((map.terrain ?? []).map(([x, z]) => `${x},${z}`));
  for (const [x, z] of map.terrain ?? []) {
    const cell = [`${x + 1},${z}`, `${x},${z + 1}`, `${x + 1},${z + 1}`];
    if (cell.every((key) => corners.has(key))) tiles.set(`${x},${z}`, terrain);
  }
  for (const placed of map.pieces) {
    const template = templates[placed.piece];
    if (!template || !FLOORS.has(placed.piece) || placed.level !== 0) continue;
    tiles.set(`${placed.x},${placed.z}`, floorColor(placed.piece, template));
  }
  const at = [...tiles.keys()].map((key) => key.split(',').map(Number));
  // A border of grass, which clamping carries out to the horizon.
  const minX = Math.min(0, ...at.map(([x]) => x)) - 1;
  const minZ = Math.min(0, ...at.map(([, z]) => z)) - 1;
  const width = Math.max(0, ...at.map(([x]) => x)) + 2 - minX;
  const height = Math.max(0, ...at.map(([, z]) => z)) + 2 - minZ;
  const data = new Float32Array(width * height * 4);
  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      const color = tiles.get(`${minX + i},${minZ + j}`);
      const { r, g, b } = color ?? ground;
      data.set([r, g, b, color === null ? 0 : 1], (j * width + i) * 4);
    }
  }
  const texture = new THREE.DataTexture(
    data,
    width,
    height,
    THREE.RGBAFormat,
    THREE.FloatType,
  );
  texture.needsUpdate = true;
  groundColors.map.value.dispose();
  groundColors.map.value = texture;
  groundColors.rect.value.set(minX, minZ, width, height);
}

/** The terrain as one mesh, tagged `userData.terrain`. */
export function buildTerrain(map: ArenaMap): THREE.Mesh {
  const { positions, indices } = terrainTriangles(map.terrain ?? []);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  // Lighter higher up, so slopes and dunes read.
  const base = new THREE.Color(map.env?.terrain ?? DEFAULT_TERRAIN);
  const colors = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    const shade = TERRAIN_SHADE + 0.04 * positions[i + 1];
    colors[i] = base.r * shade;
    colors[i + 1] = base.g * shade;
    colors[i + 2] = base.b * shade;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const mesh = new THREE.Mesh(
    geometry,
    // Double-sided so skirts show whichever way their edge was wound; offset behind floor tiles
    // lying on it.
    mottle(
      new THREE.MeshLambertMaterial({
        vertexColors: true,
        flatShading: true,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      }),
    ),
  );
  mesh.receiveShadow = true;
  mesh.castShadow = true;
  mesh.userData.terrain = true;
  return mesh;
}

/**
 * The map for play: one InstancedMesh per piece mesh, so hundreds of pieces cost a draw call per
 * kind. Gates stay separate objects so they can sink when the match goes live.
 */
export function buildMapInstances(
  map: ArenaMap,
  templates: PieceTemplates,
): { object: THREE.Group; gates: THREE.Object3D[] } {
  const object = new THREE.Group();
  const gates: THREE.Object3D[] = [];
  const placements = new Map<PieceId, THREE.Matrix4[]>();
  const dummy = new THREE.Object3D();
  for (const placed of map.pieces) {
    const template = templates[placed.piece];
    if (!template) continue;
    if (PIECES[placed.piece].collision === 'gate') {
      const gate = template.clone();
      gate.traverse((child) => (child.userData.piece = placed.piece));
      placePiece(gate, placed);
      gates.push(gate);
      object.add(gate);
      continue;
    }
    placePiece(dummy, placed);
    dummy.updateMatrix();
    const matrices = placements.get(placed.piece) ?? [];
    matrices.push(dummy.matrix.clone());
    placements.set(placed.piece, matrices);
  }
  const matrix = new THREE.Matrix4();
  for (const [id, matrices] of placements) {
    const template = templates[id]!;
    // Floors lying on the ground have nothing below to shade.
    const castShadow =
      !FLOORS.has(id) || map.pieces.some((p) => p.piece === id && p.level > 0);
    template.updateMatrixWorld(true);
    template.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.geometry.computeBoundingBox();
      const material = SWAYING.has(id)
        ? sway(
            child.material as THREE.Material,
            child.geometry.boundingBox!.max.y,
          )
        : child.material;
      const mesh = new THREE.InstancedMesh(
        child.geometry,
        material,
        matrices.length,
      );
      for (const [i, placement] of matrices.entries())
        mesh.setMatrixAt(
          i,
          matrix.multiplyMatrices(placement, child.matrixWorld),
        );
      mesh.castShadow = castShadow;
      mesh.receiveShadow = true;
      mesh.userData.piece = id;
      mesh.computeBoundingSphere();
      object.add(mesh);
    });
  }
  if (map.terrain?.length) object.add(buildTerrain(map));
  paintGround(map, templates);
  return { object, gates };
}
